import React from 'react';
import { PPTTranscriptionState } from './types';

interface PPTProcessingStatusProps {
  pptTranscription: PPTTranscriptionState;
  showExtractedContent?: boolean;
}

function formatContent(text: string) {
  // Split by sections (=== markers)
  const sections = text.split(/===\s*([^=]+)\s*===/);
  const formattedSections: Array<{ title: string; content: string }> = [];

  for (let i = 1; i < sections.length; i += 2) {
    const sectionTitle = sections[i]?.trim();
    const sectionContent = sections[i + 1]?.trim();

    if (sectionTitle && sectionContent) {
      formattedSections.push({ title: sectionTitle, content: sectionContent });
    }
  }

  return formattedSections;
}

export function PPTProcessingStatus({
  pptTranscription,
  showExtractedContent = true,
}: PPTProcessingStatusProps) {
  return (
    <div className="mt-6 space-y-4">
      <div>
        <h4 className="text-base font-baijamjuree font-semibold text-gray-900 mb-3">
          Presentation Analysis Status
        </h4>
      </div>

      {pptTranscription.isTranscribing && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="flex-1">
              <h5 className="text-base font-baijamjuree font-bold text-blue-900 mb-2">
                Processing Presentation Content
              </h5>
              <p className="text-sm text-blue-700 font-baijamjuree mb-3">
                Our AI is analyzing your presentation and extracting information
                relevant to the award criteria. This process typically takes
                30-60 seconds.
              </p>
            </div>
          </div>
        </div>
      )}

      {pptTranscription.isCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-start space-x-4 p-6 pb-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h5 className="text-base font-baijamjuree font-bold text-green-900 mb-2">
                Analysis Complete! 🎉
              </h5>
              <p className="text-sm text-green-700 font-baijamjuree mb-3">
                Your presentation has been successfully analyzed and key
                information has been extracted according to the award criteria.
              </p>
            </div>
          </div>

          {/* Extracted Content Display */}
          {showExtractedContent && (
            <div className="bg-white border-t border-green-200">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 border-b border-gray-200">
                <p className="text-sm font-baijamjuree font-medium text-blue-800">
                  📄 Extracted criteria-specific content
                </p>
              </div>

              <div className="p-6 max-h-80 overflow-y-auto">
                <ExtractedContent content={pptTranscription.extractedData!} />
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 font-baijamjuree flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  This extracted content will be included in your submission for
                  enhanced evaluation
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {pptTranscription.error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-yellow-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h5 className="text-base font-baijamjuree font-bold text-yellow-900 mb-2">
                Processing Issue Detected
              </h5>
              <p className="text-sm text-yellow-700 font-baijamjuree mb-3">
                We encountered an issue while processing your presentation:{' '}
                <strong>{pptTranscription.error}</strong>
              </p>
              <div className="bg-yellow-100 rounded-lg p-3">
                <p className="text-xs text-yellow-600 font-baijamjuree font-medium">
                  ⚠️ Don't worry - Your submission can still proceed with the
                  information you've provided in the form
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExtractedContent({ content }: { content: string }) {
  const sections = formatContent(content);

  if (sections.length > 0) {
    return (
      <div className="space-y-6">
        {sections.map((section, index) => (
          <div key={index} className="space-y-3">
            {/* Section Title */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-medium text-xs">
                  {index + 1}
                </span>
              </div>
              <h4 className="text-sm font-baijamjuree font-medium text-gray-800">
                {section.title}
              </h4>
            </div>

            {/* Section Content */}
            <div className="ml-7 space-y-3">
              {section.content
                .split('•')
                .filter(point => point.trim())
                .map((point, pointIndex) => {
                  const cleanPoint = point.trim().replace(/^\s*-\s*/, '');
                  if (!cleanPoint) return null;

                  return (
                    <div
                      key={pointIndex}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0 group-hover:bg-blue-600 transition-colors"></div>
                      <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm flex-1 group-hover:text-gray-900 transition-colors">
                        {cleanPoint}
                      </p>
                    </div>
                  );
                })}
            </div>

            {/* Divider between sections */}
            {index < sections.length - 1 && (
              <div className="border-t border-gray-100 pt-2 mt-4"></div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Fallback for content without clear sections
  const points = content.split('•').filter(point => point.trim());

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-5 h-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
          <span className="text-white text-xs">📝</span>
        </div>
        <h4 className="text-sm font-baijamjuree font-medium text-gray-800">
          Summary Information
        </h4>
      </div>

      <div className="ml-7 space-y-3">
        {points.length > 1 ? (
          points.map((point, index) => {
            const cleanPoint = point.trim().replace(/^\s*-\s*/, '');
            if (!cleanPoint) return null;

            return (
              <div key={index} className="flex items-start gap-3 group">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0 group-hover:bg-green-600 transition-colors"></div>
                <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm flex-1 group-hover:text-gray-900 transition-colors">
                  {cleanPoint}
                </p>
              </div>
            );
          })
        ) : (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 font-baijamjuree leading-relaxed text-sm whitespace-pre-wrap">
              {content}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
